import React from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import ReactEcharts from "echarts-for-react";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import TrainingPlanEditor from "components/TrainingPlanEditor/TrainingPlanEditor.jsx";
import SwipeableViews from "react-swipeable-views";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { dayjs } from "utils/time.jsx";

const weeklyDeckStyles = {
  weeklyDeck: {
    margin: "10px 0 20px"
  },
  weeklyDeckHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 16px"
  },
  chartModeControls: {
    display: "flex",
    gap: "8px",
    padding: "0 16px 8px"
  },
  chartModeButton: {
    border: "1px solid #d9e2ef",
    borderRadius: "999px",
    background: "#f7f9fc",
    color: "#46607a",
    fontSize: "11px",
    fontWeight: 600,
    padding: "6px 12px",
    cursor: "pointer"
  },
  chartModeButtonActive: {
    border: "1px solid #235894",
    background: "#235894",
    color: "#ffffff"
  },
  weekNavControls: {
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  weekNavLabel: {
    color: "#5a6a7f",
    fontSize: "12px",
    minWidth: "86px",
    textAlign: "center"
  },
  weekNavButton: {
    padding: "4px 10px",
    minWidth: "auto",
    fontSize: "11px"
  },
  weekDaysRow: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gap: "6px",
    padding: "0 16px 12px",
    "@media (max-width: 600px)": {
      gridTemplateColumns: "repeat(7, minmax(80px, 1fr))",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      paddingBottom: "8px"
    }
  },
  weekDayButton: {
    border: "1px solid #d9e2ef",
    borderRadius: "10px",
    padding: "6px 4px",
    background: "#f7f9fc",
    color: "#2b3c4d",
    cursor: "pointer",
    fontSize: "12px",
    lineHeight: "16px"
  },
  weekDayButtonActive: {
    border: "1px solid #235894",
    background: "#e7f0ff",
    color: "#235894",
    fontWeight: "600"
  },
  dayLabel: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1b2a41"
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#5a6a7f",
    marginBottom: "6px",
    textTransform: "uppercase"
  },
  trainingSection: {
    background: "#f2f7ff",
    border: "1px solid #d7e6ff",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "16px"
  },
  trainingHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "6px"
  },
  editTrainingButton: {
    padding: "3px 10px",
    minWidth: "auto",
    fontSize: "11px"
  },
  activitiesSection: {
    background: "#f6faf7",
    border: "1px solid #d9efe1",
    borderRadius: "12px",
    padding: "12px"
  },
  trainingDescription: {
    whiteSpace: "pre-wrap",
    color: "#2b3c4d",
    lineHeight: "1.5"
  },
  activityList: {
    margin: 0,
    paddingLeft: "18px",
    color: "#2b3c4d"
  },
  activityRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    marginBottom: "6px"
  },
  activityLabel: {
    flex: 1
  },
  activityNoteButton: {
    padding: "3px 10px",
    minWidth: "auto",
    fontSize: "11px"
  },
  notesSection: {
    background: "#fff3e0",
    border: "1px solid #ffe0b2",
    borderRadius: "12px",
    padding: "12px",
    marginBottom: "16px"
  },
  noteItem: {
    marginBottom: "10px",
    padding: "8px",
    background: "white",
    borderRadius: "8px",
    border: "1px solid #f5deb3"
  },
  noteTitle: {
    fontWeight: "600",
    color: "#2b3c4d",
    marginBottom: "4px",
    fontSize: "13px"
  },
  noteContent: {
    color: "#5a6a7f",
    fontSize: "12px",
    lineHeight: "1.4",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  noteMetadata: {
    fontSize: "11px",
    color: "#9a9a9a",
    marginTop: "6px",
    fontStyle: "italic"
  },
  chartContainer: {
    padding: "16px",
    marginBottom: "16px"
  }
};

class WeeklyDeck extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeDayIndex: dayjs().day(),
      editorOpen: false,
      editorDateKey: "",
      editorTraining: null,
      savingTrainingPlan: false,
      trainingPlanError: "",
      distanceChartMode: "daily"
    };
  }

  openTrainingEditor = (dateKey, training) => {
    this.setState({
      editorOpen: true,
      editorDateKey: dateKey,
      editorTraining: training,
      trainingPlanError: ""
    });
  };

  closeTrainingEditor = () => {
    this.setState({
      editorOpen: false,
      editorDateKey: "",
      editorTraining: null,
      savingTrainingPlan: false,
      trainingPlanError: ""
    });
  };

  saveTrainingEditor = async ({ title, description }) => {
    const { onUpdateTrainingPlan } = this.props;
    const { editorDateKey } = this.state;

    if (!editorDateKey) {
      return;
    }

    this.setState({ savingTrainingPlan: true, trainingPlanError: "" });
    try {
      if (onUpdateTrainingPlan) {
        await onUpdateTrainingPlan({
          day: editorDateKey,
          title,
          description
        });
      }
      this.closeTrainingEditor();
    } catch (error) {
      this.setState({
        savingTrainingPlan: false,
        trainingPlanError: "Failed to update training plan. Please try again."
      });
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.weekOffset !== this.props.weekOffset) {
      this.setState({
        activeDayIndex: this.props.weekOffset === 0 ? dayjs().day() : 0
      });
    }
  }

  getWeekDays = () => {
    const weekStart = dayjs()
      .startOf("week")
      .add(this.props.weekOffset || 0, "week");
    return Array.from({ length: 7 }, (_, index) => weekStart.add(index, "day"));
  };

  getWidgetDateKey = widgetData => {
    if (!widgetData || !widgetData.widget) return "";
    const { widget } = widgetData;
    return dayjs(widget.day).format("YYYY-MM-DD");
  };

  splitWidgetsByDate = dashboardWidgets => {
    const trainingByDate = {};
    const activitiesByDate = {};

    dashboardWidgets.forEach(widgetData => {
      if (!widgetData || !widgetData.widget) return;
      const dateKey = this.getWidgetDateKey(widgetData);
      if (!dateKey) return;

      if (
        widgetData.widget.source === "user_calender" &&
        widgetData.widget.type === "Training"
      ) {
        if (!trainingByDate[dateKey]) {
          trainingByDate[dateKey] = [];
        }
        trainingByDate[dateKey].push(widgetData);
      }

      if (
        widgetData.widget.source === "user_activities" &&
        widgetData.widget.type === "activity"
      ) {
        if (!activitiesByDate[dateKey]) {
          activitiesByDate[dateKey] = [];
        }
        activitiesByDate[dateKey].push(widgetData);
      }
    });

    return { trainingByDate, activitiesByDate };
  };

  groupNotesByDate = notes => {
    const notesByDate = {};

    if (!notes || !Array.isArray(notes)) {
      return notesByDate;
    }

    notes.forEach(note => {
      if (!note || !note.date) return;
      const dateKey = dayjs(note.date).format("YYYY-MM-DD");
      if (!notesByDate[dateKey]) {
        notesByDate[dateKey] = [];
      }
      notesByDate[dateKey].push(note);
    });

    return notesByDate;
  };

  getWeeklyDistanceSummary = dashboardWidgets => {
    const summaryWidget = dashboardWidgets.find(widgetData => {
      const widget = widgetData && widgetData.widget;
      if (!widget) return false;
      return (
        widget.type === "weekly_training_plan_summary" ||
        widget.source === "weekly_training_plan_summary"
      );
    });

    const metadata =
      summaryWidget && summaryWidget.widget
        ? summaryWidget.widget.metadata
        : null;

    return {
      plannedKm:
        metadata && metadata.distance_planned_km != null
          ? metadata.distance_planned_km
          : null,
      completedKm:
        metadata && metadata.distance_completed_km != null
          ? metadata.distance_completed_km
          : null,
      details: metadata && metadata.details ? metadata.details : null
    };
  };

  handleActiveDayChange = index => {
    this.setState({ activeDayIndex: index });
  };

  cleanTrainingDescription = description => {
    if (!description) return "";
    return description
      .replace(/^"+|"+$/g, "")
      .replace(/\\"/g, '"')
      .replace(/\s*;{3,}\s*/g, "\n")
      .trim();
  };

  renderDailyDistanceChart = details => {
    if (!details) return null;

    const dates = Object.keys(details).sort();
    const plannedData = dates.map(
      date => details[date].planned_distance_km || 0
    );
    const completedData = dates.map(
      date => details[date].completed_distance_km || 0
    );
    const labels = dates.map(date => dayjs(date).format("MMM D"));

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow"
        },
        formatter: params => {
          let result = `<strong>${params[0].name}</strong><br/>`;
          params.forEach(param => {
            result += `${param.marker} ${
              param.seriesName
            }: ${param.value.toFixed(1)} km<br/>`;
          });
          return result;
        }
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "3%",
        containLabel: true
      },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: {
          rotate: 0,
          fontSize: 11
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: "value",
        axisLabel: {
          show: false
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        }
      },
      series: [
        {
          name: "Completed",
          type: "bar",
          data: completedData,
          itemStyle: {
            color: "#235894"
          },
          barMaxWidth: 50
        },
        {
          name: "Planned",
          type: "line",
          data: plannedData,
          lineStyle: {
            color: "#9ec5fe",
            width: 2
          },
          itemStyle: {
            color: "#9ec5fe"
          },
          symbol: "circle",
          symbolSize: 6
        }
      ]
    };

    return (
      <div className={this.props.classes.chartContainer}>
        <ReactEcharts option={option} style={{ height: "100px" }} />
      </div>
    );
  };

  renderWeeklyTotalChart = history => {
    if (!history || history.length === 0) {
      return null;
    }

    const labels = history.map(week => week.weekLabel);
    const completedData = history.map(week => week.completedKm || 0);

    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow"
        },
        formatter: params => {
          const point = history[params[0].dataIndex];
          return `<strong>Week of ${
            point.weekLabel
          }</strong><br/>Completed: ${point.completedKm.toFixed(1)} km`;
        }
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        top: "3%",
        containLabel: true
      },
      xAxis: {
        type: "category",
        data: labels,
        axisLabel: {
          rotate: 0,
          fontSize: 11
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: "value",
        axisLabel: {
          show: true,
          formatter: value => `${value} km`
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        }
      },
      series: [
        {
          name: "Weekly total",
          type: "bar",
          data: completedData,
          itemStyle: {
            color: "#235894"
          },
          barMaxWidth: 40
        }
      ]
    };

    return (
      <div className={this.props.classes.chartContainer}>
        <ReactEcharts option={option} style={{ height: "160px" }} />
      </div>
    );
  };

  render() {
    const {
      classes,
      dashboardWidgets,
      userNotes,
      weekOffset,
      widgetsLoading,
      onChangeWeek,
      onGoToCurrentWeek,
      onAddActivityNote,
      onUpdateTrainingPlan
    } = this.props;
    const {
      activeDayIndex,
      editorOpen,
      editorDateKey,
      editorTraining,
      savingTrainingPlan,
      trainingPlanError,
      distanceChartMode
    } = this.state;
    const weekDays = this.getWeekDays();
    const { trainingByDate, activitiesByDate } = this.splitWidgetsByDate(
      dashboardWidgets
    );
    const notesByDate = this.groupNotesByDate(userNotes);
    const { plannedKm, completedKm, details } = this.getWeeklyDistanceSummary(
      dashboardWidgets
    );
    const { weeklyMileageHistory, weeklyMileageHistoryLoading } = this.props;
    const summaryLabel =
      plannedKm != null && completedKm != null
        ? `${
            weekOffset === 0 ? "This week" : "Selected week"
          } • Planned ${plannedKm.toFixed(
            1
          )} km • Completed ${completedKm.toFixed(1)} km`
        : weekOffset === 0
          ? "This week"
          : "Selected week";

    return (
      <GridItem xs={12} sm={12} md={12}>
        <Card className={classes.weeklyDeck}>
          <div className={classes.weeklyDeckHeader}>
            <div className={classes.dayLabel}>{summaryLabel}</div>
            <div className={classes.weekNavControls}>
              <Button
                color="info"
                simple
                size="sm"
                className={classes.weekNavButton}
                onClick={() => onChangeWeek && onChangeWeek(-1)}
                disabled={widgetsLoading}
              >
                <NavigateBeforeIcon />
                Prev
              </Button>
              <div className={classes.weekNavLabel}>
                {weekOffset === 0
                  ? "Current"
                  : `Week ${weekOffset > 0 ? "+" : ""}${weekOffset}`}
              </div>
              <Button
                color="info"
                simple
                size="sm"
                className={classes.weekNavButton}
                onClick={() => onChangeWeek && onChangeWeek(1)}
                disabled={widgetsLoading}
              >
                Next
                <NavigateNextIcon />
              </Button>
              {weekOffset !== 0 && (
                <Button
                  color="warning"
                  simple
                  size="sm"
                  className={classes.weekNavButton}
                  onClick={() => onGoToCurrentWeek && onGoToCurrentWeek()}
                  disabled={widgetsLoading}
                >
                  Today
                </Button>
              )}
            </div>
          </div>
          <div
            style={{
              color: "#5a6a7f",
              fontSize: "12px",
              padding: "0 16px 8px"
            }}
          >
            {weekDays[0].format("MMM D")} - {weekDays[6].format("MMM D")}
            {widgetsLoading ? " • Loading..." : ""}
          </div>
          <div className={classes.chartModeControls}>
            <button
              type="button"
              className={`${classes.chartModeButton} ${
                distanceChartMode === "daily"
                  ? classes.chartModeButtonActive
                  : ""
              }`}
              onClick={() => this.setState({ distanceChartMode: "daily" })}
            >
              Weekdays
            </button>
            <button
              type="button"
              className={`${classes.chartModeButton} ${
                distanceChartMode === "weekly"
                  ? classes.chartModeButtonActive
                  : ""
              }`}
              onClick={() => this.setState({ distanceChartMode: "weekly" })}
            >
              Weekly totals
            </button>
          </div>
          {distanceChartMode === "daily" &&
            details &&
            this.renderDailyDistanceChart(details)}
          {distanceChartMode === "weekly" &&
            (weeklyMileageHistoryLoading ? (
              <div style={{ padding: "0 16px 12px", color: "#7a8a9f" }}>
                Loading weekly totals...
              </div>
            ) : (
              this.renderWeeklyTotalChart(weeklyMileageHistory)
            ))}
          <div className={classes.weekDaysRow}>
            {weekDays.map((day, index) => {
              const isActive = index === activeDayIndex;
              return (
                <button
                  key={day.format("YYYY-MM-DD")}
                  type="button"
                  className={
                    isActive
                      ? `${classes.weekDayButton} ${
                          classes.weekDayButtonActive
                        }`
                      : classes.weekDayButton
                  }
                  onClick={() => this.handleActiveDayChange(index)}
                >
                  <div>{day.format("ddd")}</div>
                  <div>{day.format("MMM D")}</div>
                </button>
              );
            })}
          </div>
          <CardBody>
            <SwipeableViews
              index={activeDayIndex}
              onChangeIndex={this.handleActiveDayChange}
              resistance
              style={{ overflowY: "hidden" }}
            >
              {weekDays.map(day => {
                const dateKey = day.format("YYYY-MM-DD");
                const trainings = trainingByDate[dateKey] || [];
                const activities = activitiesByDate[dateKey] || [];
                const training = trainings[0] ? trainings[0].widget : null;

                return (
                  <div key={dateKey}>
                    <div style={{ padding: "12px 16px" }}>
                      <div className={classes.trainingSection}>
                        <div className={classes.trainingHeader}>
                          <div className={classes.sectionTitle}>Training</div>
                          {training &&
                            onUpdateTrainingPlan && (
                              <Button
                                color="info"
                                simple
                                size="sm"
                                className={classes.editTrainingButton}
                                onClick={() =>
                                  this.openTrainingEditor(dateKey, training)
                                }
                              >
                                Edit Plan
                              </Button>
                            )}
                        </div>
                        {training ? (
                          <div>
                            <div style={{ fontWeight: "600" }}>
                              {training.name}
                            </div>
                            <div className={classes.trainingDescription}>
                              {this.cleanTrainingDescription(
                                training.description
                              ) || "No details available."}
                            </div>
                          </div>
                        ) : (
                          <div style={{ color: "#7a8a9f" }}>
                            No training planned for this day.
                          </div>
                        )}
                      </div>

                      <div className={classes.activitiesSection}>
                        <div className={classes.sectionTitle}>Activities</div>
                        {activities.length ? (
                          <ul className={classes.activityList}>
                            {activities.map(activity => {
                              const meta = activity.widget.metadata || {};
                              const distance =
                                meta.distance_km != null
                                  ? `${meta.distance_km.toFixed(1)} km`
                                  : "";
                              const timeLabel = meta.start_time
                                ? dayjs(meta.start_time).format("h:mm A")
                                : "";
                              const details = [distance, timeLabel]
                                .filter(Boolean)
                                .join(" • ");
                              const activityWidget = activity.widget;
                              return (
                                <li key={`${activityWidget.name}-${dateKey}`}>
                                  <div className={classes.activityRow}>
                                    <span className={classes.activityLabel}>
                                      <strong>
                                        {activityWidget.description}
                                      </strong>
                                      {details ? ` — ${details}` : ""}
                                    </span>
                                    <Button
                                      color="info"
                                      simple
                                      size="sm"
                                      className={classes.activityNoteButton}
                                      onClick={() =>
                                        onAddActivityNote &&
                                        onAddActivityNote(
                                          activityWidget,
                                          training
                                        )
                                      }
                                    >
                                      Add Note
                                    </Button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <div style={{ color: "#7a8a9f" }}>
                            No activities logged.
                          </div>
                        )}
                      </div>

                      {notesByDate[dateKey] &&
                        notesByDate[dateKey].length > 0 && (
                          <div className={classes.notesSection}>
                            <div className={classes.sectionTitle}>Notes</div>
                            {notesByDate[dateKey].map(note => (
                              <div
                                key={`note-${note.id}`}
                                className={classes.noteItem}
                              >
                                <div className={classes.noteTitle}>
                                  {note.title}
                                </div>
                                {note.notes && (
                                  <div className={classes.noteContent}>
                                    {note.notes}
                                  </div>
                                )}
                                {note.activity_name && (
                                  <div className={classes.noteMetadata}>
                                    Activity: {note.activity_name}
                                  </div>
                                )}
                                {note.created_at && (
                                  <div className={classes.noteMetadata}>
                                    Added on{" "}
                                    {dayjs(note.created_at).format(
                                      "MMM D, h:mm A"
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                );
              })}
            </SwipeableViews>
          </CardBody>
        </Card>
        <TrainingPlanEditor
          open={editorOpen}
          onClose={this.closeTrainingEditor}
          onSave={this.saveTrainingEditor}
          initialTitle={editorTraining ? editorTraining.name : ""}
          initialDescription={
            editorTraining
              ? this.cleanTrainingDescription(editorTraining.description)
              : ""
          }
          dayLabel={
            editorDateKey ? dayjs(editorDateKey).format("dddd, MMM D") : ""
          }
          saving={savingTrainingPlan}
          error={trainingPlanError}
        />
      </GridItem>
    );
  }
}

WeeklyDeck.propTypes = {
  classes: PropTypes.object.isRequired,
  dashboardWidgets: PropTypes.array.isRequired,
  userNotes: PropTypes.array,
  weeklyMileageHistory: PropTypes.array,
  weeklyMileageHistoryLoading: PropTypes.bool,
  weekOffset: PropTypes.number,
  widgetsLoading: PropTypes.bool,
  onChangeWeek: PropTypes.func,
  onGoToCurrentWeek: PropTypes.func,
  onAddActivityNote: PropTypes.func,
  onUpdateTrainingPlan: PropTypes.func
};

export default withStyles(weeklyDeckStyles)(WeeklyDeck);
